import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Card, PrimaryButton, ProgressBar, SectionLabel } from "../../components/ui";
import { estimateKnown, startingBand, type LevelAnswer } from "../../lib/level-test";
import { computeQuota } from "../../lib/quota";
import { createStarterDeck } from "../../lib/deck";
import { WORDS } from "../../data/words";
import { EXAM_PROFILES } from "../../data/exam-profiles";
import { corpusLabel } from "../../data/corpus";
import { useAppStore } from "../../state/app-store-context";

/** 使用者尚未設定考試日期時的預設學習期（120 天） */
function fallbackDeadline(today: string): string {
  const d = new Date(today);
  d.setDate(d.getDate() + 120);
  return d.toISOString().slice(0, 10);
}

const SEGMENTS: { band: number; label: string }[] = [
  { band: 1, label: "1–1k" },
  { band: 2, label: "1–2k" },
  { band: 3, label: "2–3k" },
  { band: 4, label: "3k+" },
];

/** 各起点 band 视为已掌握的词数(跳过的词量) */
const BAND_KNOWN_FLOOR: Record<number, number> = { 1: 0, 2: 1000, 3: 2000, 4: 3000 };

/** 按 band 动态显示的结果页标题 */
const BAND_TITLE: Record<number, string> = {
  1: "你正在打基礎",
  2: "你的起點在初階水平",
  3: "你的起點在DSE 中上水平",
  4: "你的起點在進階水平",
};

const fmt = (n: number) => n.toLocaleString("en-US");

export function Result() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, dispatch } = useAppStore();
  const answers = (location.state as { answers?: LevelAnswer[] } | null)?.answers;

  if (!answers || answers.length === 0) {
    return <Navigate to="/level-test/intro" replace />;
  }

  const known = estimateKnown(answers);
  const band = startingBand(known);
  const today = new Date().toISOString().slice(0, 10);
  const profile = EXAM_PROFILES[state.targetExam];
  const deadline = state.targetDeadline ?? fallbackDeadline(today);
  const corpusTotal = profile.corpusWordIds.length;
  const quota = computeQuota({ total: corpusTotal, learned: known, deadline, today });
  const skipped = BAND_KNOWN_FLOOR[band];
  const currentSeg = SEGMENTS[band - 1];
  const learnDays = quota.remaining > 0 ? Math.ceil(quota.remaining / quota.newPerDay) : 0;
  const reviewDays = Math.max(0, quota.daysLeft - learnDays);

  return (
    <div className="min-h-dvh box-border bg-cream flex flex-col pt-[clamp(40px,7vh,64px)] px-[clamp(18px,6vw,28px)] pb-[clamp(20px,4vh,36px)]">
      <div className="font-grotesk text-[11px] font-semibold tracking-[.16em] text-ink4">RESULT · 測試完成</div>
      <h2 className="mt-[12px] mb-0 text-[25px] leading-[1.35] font-bold text-ink">
        {BAND_TITLE[band]}
      </h2>

      <div className="hero-card mt-[22px] p-[22px] rounded-[18px]">
        <div className="text-[12.5px] text-cream/60">估計已掌握詞彙量</div>
        <div className="flex items-baseline gap-[8px] mt-[6px]">
          <span className="font-grotesk text-[46px] font-semibold text-lime leading-none tracking-[-.03em]">
            {fmt(known)}
          </span>
          <span className="text-[15px] text-cream/60">± 180 詞</span>
        </div>
        <div className="mt-[18px] mb-[9px]">
          <ProgressBar pct={(known / corpusTotal) * 100} h={6} barClass="bg-lime" trackClass="bg-cream/16" />
        </div>
        <div className="flex justify-between font-grotesk text-[11.5px] text-cream/50">
          <span>0</span>
          <span>目標 {fmt(corpusTotal)} 詞</span>
        </div>
      </div>

      <Card className="mt-[12px] p-[18px] rounded-2xl">
        <SectionLabel>起點 STARTING BAND</SectionLabel>
        <div className="flex gap-[5px] mt-[12px]">
          {SEGMENTS.map((seg) => {
            const isCurrent = seg.band === band;
            const isBelow = seg.band < band;
            return (
              <div
                key={seg.band}
                style={{ flex: isCurrent ? 1.3 : 1 }}
                className={`h-[34px] rounded-[6px] flex items-center justify-center font-grotesk text-[11.5px] ${
                  isCurrent
                    ? "bg-slate-900 dark:bg-slate-800 dark:border dark:border-lime/50 font-semibold text-lime"
                    : isBelow
                      ? "bg-green-bg border border-green-line text-green-text"
                      : "bg-paper border border-line-tab text-ink4"
                }`}
              >
                {seg.label}{isCurrent ? " ←" : ""}
              </div>
            );
          })}
        </div>
        <div className="text-[13px] leading-[1.65] text-ink2 mt-[13px]">
          跳過前 {fmt(skipped)} 個你已經會的詞，直接從 {currentSeg.label} 級開始。之後每 200 詞會複測一次。
        </div>
      </Card>

      <Card className="mt-[12px] p-[18px] rounded-2xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[15px] font-bold text-ink">建議每日 {quota.newPerDay} 個新詞</div>
            <div className="text-[12.5px] text-ink2 mt-[3px]">
              {fmt(quota.remaining)} 個未學的詞 ÷ {quota.newPerDay} = {learnDays} 天學完，其餘 {reviewDays} 天全部用來複習
            </div>
          </div>
          <span className="font-grotesk text-[13px] text-accent shrink-0">調整 ›</span>
        </div>
        <div className="h-px bg-line-soft my-[14px]" />
        <div className="flex items-center justify-between">
          <span className="text-[14.5px] text-ink">第一個詞單</span>
          <span className="font-grotesk text-[13.5px] text-ink2">{corpusLabel(profile.label)} ›</span>
        </div>
      </Card>

      <div className="mt-auto pt-[clamp(18px,3vh,28px)] flex flex-col gap-[10px]">
        <PrimaryButton
          onClick={() => {
            const now = new Date().toISOString();
            const starterDeck = createStarterDeck({
              exam: state.targetExam,
              wordIds: WORDS.map((word) => word.id),
              dailyNew: quota.newPerDay,
              now,
            });
            if (!state.decks.some((deck) => deck.id.startsWith(`starter-${state.targetExam}-`))) {
              dispatch({ type: "addDeck", deck: starterDeck });
            }
            dispatch({ type: "updateSettings", partial: { dailyGoal: quota.newPerDay } });
            dispatch({ type: "setOnboarded", value: true });
            navigate("/today");
          }}
        >
          開始今天的 {quota.newPerDay} 個詞
        </PrimaryButton>
        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate("/level-test/intro")}
            className="text-[13.5px] text-ink2 bg-transparent border-0 p-0 cursor-pointer"
          >
            重新測一次
          </button>
        </div>
      </div>
    </div>
  );
}
